import React, { useState } from "react";
import { Box, CardHeader, Rating, Avatar, Typography, IconButton, Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import TimelineIcon from "@mui/icons-material/Timeline";
import Done from "@mui/icons-material/Done";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router";
import PublicVisibilityDialog from "../common/PublicVisibilityDialog";

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: "12px 60px 12px 16px", // Add right padding for menu icon
  background: "rgba(255,255,255,0.9)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(226,232,240,0.3)",
  borderRadius: "16px 16px 0 0",
  overflow: "hidden",
  "& .MuiCardHeader-title": {
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 50px 10px 12px",
  },
}));

const UserNameBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: "8px",
  background: "rgba(99,102,241,0.05)",
  border: "1px solid rgba(99,102,241,0.1)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "rgba(99,102,241,0.1)",
    borderColor: "rgba(99,102,241,0.2)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(99,102,241,0.15)",
  },
  "& label": {
    fontSize: "12px",
    fontWeight: 600,
    color: "rgba(30,41,59,0.9)",
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: "28px",
  height: "28px",
  marginRight: "8px",
  border: "2px solid rgba(255,255,255,0.9)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
  },
}));

const AgentAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  flexShrink: 0,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)",
  backgroundSize: "200% 200%",
  animation: "gradientShift 3s ease infinite",
  border: "2px solid rgba(255,255,255,0.9)",
  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "scale(1.08) rotate(5deg)",
    boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5), inset 0 1px 0 rgba(255,255,255,0.4)",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
    fontSize: "18px",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
  },
  [(theme) => theme.breakpoints.down("sm")]: {
    width: 28,
    height: 28,
    "& .MuiSvgIcon-root": {
      fontSize: "16px",
    },
  },
  "@keyframes gradientShift": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));

const HomeCardHeader = ({
  data,
  isFeedback,
  displayStars,
  isBookmark,
  onRatingClick,
  showRatePopup,
  onPathClick,
  navigate,
  isCommunity = false,
  onVisibilityUpdate,
}) => {
  const navigateHook = useNavigate();
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const isSystemAgent = data?.user_name === "system_recommendation_agent";

  const handleProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.user_name && !isSystemAgent) {
      navigateHook(`/user/${data.user_name}`);
    }
  };

  const handleVisibilityClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setVisibilityDialogOpen(true);
  };

  const handleVisibilityUpdate = (newIsPublic) => {
    if (onVisibilityUpdate) {
      onVisibilityUpdate(newIsPublic);
    }
  };

  const getDisplayName = () => {
    if (isSystemAgent) return "Datarate Agent";
    
    // Use display_name from backend if available (first_name + last_name)
    if (data.display_name) {
      return data.display_name;
    }
    
    // Fallback to constructing from first_name/last_name for backward compatibility
    const firstName = (data.first_name || "").trim();
    const lastName = (data.last_name || "").trim();
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) return firstName;
    if (lastName) return lastName;
    
    // Only fall back to user_name if no first/last name available
    return data.user_name || "Anonymous User";
  };

  return (
    <>
    <StyledCardHeader
      title={
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
              gap: 1,
            }}
          >
            {/* Profile Image and Name Section */}
            <Box 
              onClick={handleProfileClick}
              data-profile-click="true"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                minWidth: 0,
                flex: '1 1 auto',
                overflow: 'hidden',
                cursor: isSystemAgent ? 'default' : 'pointer',
              }}
            >
              {isSystemAgent ? (
                <AgentAvatar>
                  <SmartToyIcon />
                </AgentAvatar>
              ) : (
                <Avatar
                  alt={data.user_name || "User"}
                  src={data.profile_image_url}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    flexShrink: 0,
                    cursor: isSystemAgent ? 'default' : 'pointer',
                    [(theme) => theme.breakpoints.down("sm")]: {
                      width: 28,
                      height: 28,
                    },
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '14px', 
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: isSystemAgent ? 'default' : 'pointer',
                    [(theme) => theme.breakpoints.down("sm")]: {
                      fontSize: '12px',
                    },
                  }}
                >
                  {getDisplayName()}
                </Typography>
                {isBookmark && displayStars && data.average_stars > 0 && (
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.25,
                    mb: 0.25,
                  }}>
                    <Rating
                      value={data.average_stars}
                      readOnly
                      size="small"
                      emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                      sx={{ 
                        color: "rgba(255, 204, 72, 1)", 
                        fontSize: '12px',
                        '& .MuiRating-icon': {
                          fontSize: '12px',
                          [(theme) => theme.breakpoints.down("sm")]: {
                            fontSize: '10px',
                          },
                        },
                      }}
                    />
                  </Box>
                )}
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '12px',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    [(theme) => theme.breakpoints.down("sm")]: {
                      fontSize: '10px',
                    },
                  }}
                >
                  {new Date(data.create_time || Date.now()).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>
            
            {/* Right side - Rating and Feedback Icons */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 0.5,
              flexShrink: 0,
              minWidth: 0,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}>
              {!isCommunity && data.is_public && (
                <Tooltip title="Click to change visibility" arrow>
                  <IconButton
                    size="small"
                    onClick={handleVisibilityClick}
                    sx={{
                      color: "#6366f1",
                      p: 0.5,
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "rgba(99, 102, 241, 0.1)",
                        color: "#6366f1",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                        [(theme) => theme.breakpoints.down("sm")]: {
                          fontSize: "14px",
                        },
                      },
                    }}
                  >
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {!isCommunity && !data.is_public && (
                <Tooltip title="Click to change visibility" arrow>
                  <IconButton
                    size="small"
                    onClick={handleVisibilityClick}
                    sx={{
                      color: "#6366f1",
                      p: 0.5,
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "rgba(99, 102, 241, 0.1)",
                        color: "#6366f1",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                        [(theme) => theme.breakpoints.down("sm")]: {
                          fontSize: "14px",
                        },
                      },
                    }}
                  >
                    <VisibilityOffIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {isFeedback && data.resolved && (
                <Tooltip title="The customer received an answer" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      color: "#10b981",
                      background: "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(34,197,94,0.1) 100%)",
                      border: "1px solid rgba(16,185,129,0.2)",
                      borderRadius: "8px",
                      width: "24px",
                      height: "24px",
                      flexShrink: 0,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background: "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(34,197,94,0.15) 100%)",
                        borderColor: "rgba(16,185,129,0.3)",
                        transform: "scale(1.1)",
                        boxShadow: "0 4px 12px rgba(16,185,129,0.2)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                        [(theme) => theme.breakpoints.down("sm")]: {
                          fontSize: "14px",
                        },
                      },
                    }}
                  >
                    <Done />
                  </IconButton>
                </Tooltip>
              )}

              {isFeedback && (
                <Tooltip title="View timeline" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onPathClick) onPathClick();
                    }}
                    sx={{
                      color: "#6366f1",
                      background: "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)",
                      border: "1px solid rgba(99,102,241,0.2)",
                      borderRadius: "8px",
                      width: "24px",
                      height: "24px",
                      flexShrink: 0,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background: "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)",
                        borderColor: "rgba(99,102,241,0.3)",
                        transform: "scale(1.1)",
                        boxShadow: "0 4px 12px rgba(99,102,241,0.2)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                        [(theme) => theme.breakpoints.down("sm")]: {
                          fontSize: "14px",
                        },
                      },
                    }}
                  >
                    <TimelineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      }
    />
    {visibilityDialogOpen && (
      <PublicVisibilityDialog
        open={visibilityDialogOpen}
        onClose={() => setVisibilityDialogOpen(false)}
        ratingId={data.rating_id || data.id}
        currentIsPublic={data.is_public}
        onUpdate={handleVisibilityUpdate}
      />
    )}
    </>
  );
};

export default HomeCardHeader;
